import { Injectable } from '@angular/core';
import { SelectProjectGroupComponent } from '@project-navigation/instances/select-project-group.component';
import {UserModel } from '@user/model/user.model';
import { ConstructionProjectLoadComponent } from '@project/components/project/construction/construction-project-load.component';
import { WindowService } from '@shared/window/window.service';
import { LoadProjectComponent } from './instances/load-project.component';
import { SaveProjectComponent } from './instances/save-project.component';
import { SaveCsvComponent } from './instances/save-csv.component';
import { ExportCaeComponent } from './instances/export-cae.component';
import { ImportCaeComponent } from './instances/import-cae.component';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ImportProserveComponent } from './instances/import-proserve.component';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { CopyProjectOperationProperties } from '@project/components/project/copy/copy.component';
import { ProjectNavigationResult, ProjectNavigationTab } from './project-navigation.model';
import { CopyProjectComponent } from './instances/copy-project.component';
import { MoveProjectComponent } from './instances/move-project.component';
import { CompareListComponent } from './instances/compare-list.component';
import { RequestMultipleQuoteComponent } from './instances/request-multiple-quote.component';
import { AlertHelper } from '@shared/alert/alert.helper';
import { MoveOrCopyOperationResult } from './service/project-navigation.service';

@Injectable({ providedIn: "root"})
export class ProjectNavigationPresenter {

  constructor(
    private userModel: UserModel,
    private windowService: WindowService,
    private alertHelper: AlertHelper,
  ) {}

  public openLoadProjectDialog(data?: ProjectNavigationAdditionalData) {
    if (
      this.userModel.isConstruction &&
      (!this.userModel.user || this.userModel.user.isProduction())
    ) {
      return this.windowService.showModalFullscreenFinish(ConstructionProjectLoadComponent);
    }
    return this.windowService.showModalFullscreenFinish(LoadProjectComponent, { data: data || {} });
  }

  public async openSaveProjectDialog() {
    const projectNavigationResult = await this.windowService.showModalFullscreenFinish(SaveProjectComponent);
    try {
      return this.extractResult(projectNavigationResult)
    } catch (ex) {
      await this.alertHelper.showError('save.title.error_saving_configuration', 'save.message.error_saving_configuration');
    }
  }

  public openSaveCsvDialog() {
    return this.windowService.showModalFullscreenFinish(SaveCsvComponent);
  }

  public openExportCaeDialog() {
    return this.windowService.showModalFullscreenFinish(ExportCaeComponent);
  }

  public async openImportCaeDialog() {
    const projectNavigationResult = await this.windowService.showModalFullscreenFinish(
        ImportCaeComponent
      );
    return this.extractResult(projectNavigationResult);
  }

  public openImportProserveDialog(document: Document) {
    return this.windowService.showModalFullscreenFinish(
        ImportProserveComponent,
        { document }
      );
  }

  public async openCopyProjectDialog(
    itemToCopy: ProjectGroupVO | ProjectHeadVO,
    isCompositeArticleProject: boolean,
    copyProperties: CopyProjectOperationProperties,
    initialTab: ProjectNavigationTab
  ): Promise<MoveOrCopyOperationResult> {
    const projectNavigationResult= await this.windowService.showModalFullscreenFinish(
      CopyProjectComponent,
      {
        itemToCopy: itemToCopy,
        copyProperties: copyProperties,
        initialTab: initialTab,
        isCompositeArticleProject: isCompositeArticleProject
      }
    );
    return this.extractResult(projectNavigationResult);
  }

  public async openMoveProjectDialog(
    itemToMove: ProjectGroupVO | ProjectHeadVO,
    isCompositeArticleProject: boolean,
    initialTab: ProjectNavigationTab
  ): Promise<MoveOrCopyOperationResult> {
    const projectNavigationResult = await this.windowService.showModalFullscreenFinish(MoveProjectComponent,
      {
        itemToMove: itemToMove,
        initialTab: initialTab,
        isCompositeArticleProject: isCompositeArticleProject
      }
    );
    return this.extractResult(projectNavigationResult);
  }

  public openCompareItemListDialog() {
    return this.windowService.showModalFullscreenFinish(CompareListComponent);
  }

  public openRequestMultipleQuoteDialog() {
    return this.windowService.showModalFullscreenFinish(RequestMultipleQuoteComponent);
  }

  public openSelectProjectGroupDialog(complexArticleMode: boolean) {
    return this.windowService.showModalFullscreenFinish(SelectProjectGroupComponent, {complexArticleMode});
  }

  private extractResult<TResult>(projectNavigationResult: ProjectNavigationResult<TResult>) {
    if(projectNavigationResult.success) {
      return projectNavigationResult.result;
    }
    throw new Error('ProjectNavigation action was not completed successfully');
  }
}

export type ProjectNavigationAdditionalData = {
  navigateToConfigurator?: boolean,
  newProjectCreation?: boolean,
  fromDashBoard?: boolean
}
